/**
 * Panel registry — one entry per panel, named explicitly so a folder that never
 * reaches the map is a compile error rather than a panel missing from the app.
 *
 * Coexistence rules live in `./exclusions.ts`, NOT in the panel configs.
 * The model is default-deny: two panels can coexist only if they share a
 * COMPATIBILITY_GROUPS entry or one of them is in UNIVERSAL_PANELS.
 * After loading all panels we materialise each panel's `config.excludes`
 * (= "every other registered panel that I am NOT compatible with").
 * Resolvers (resolveOpen, etc.) keep reading `config.excludes` the same
 * way they always did.
 */

import type { ComponentType } from 'react';
import { lazy } from 'react';

import agentConfig from '@/panels/agent/panel.config';
import aiChatConfig from '@/panels/ai_chat/panel.config';
import aiSearchConfig from '@/panels/ai_search/panel.config';
import auditLogConfig from '@/panels/audit_log/panel.config';
import bookmarksConfig from '@/panels/bookmarks/panel.config';
import cameraSettingsConfig from '@/panels/camera_settings/panel.config';
import cameraViewConfig from '@/panels/camera_view/panel.config';
import camerasListConfig from '@/panels/cameras_list/panel.config';
import caseDetailsConfig from '@/panels/case_details/panel.config';
import caseDocumentConfig from '@/panels/case_document/panel.config';
import caseImageConfig from '@/panels/case_image/panel.config';
import caseVideoConfig from '@/panels/case_video/panel.config';
import enrolledObjectsConfig from '@/panels/enrolled_objects/panel.config';
import eventsListConfig from '@/panels/events_list/panel.config';
import healthMonitorConfig from '@/panels/health_monitor/panel.config';
import motionListConfig from '@/panels/motion_list/panel.config';
import notificationsConfig from '@/panels/notifications/panel.config';
import objectsListConfig from '@/panels/objects_list/panel.config';
import organizationSettingsConfig from '@/panels/organization_settings/panel.config';
import ruleEstimateConfig from '@/panels/rule_estimate/panel.config';
import ruleFlowConfig from '@/panels/rule_flow/panel.config';
import ruleFlowDetailsConfig from '@/panels/rule_flow_details/panel.config';
import rulesConfig from '@/panels/rules/panel.config';
import serversConfig from '@/panels/servers/panel.config';
import userDetailsConfig from '@/panels/user_details/panel.config';
import usersConfig from '@/panels/users/panel.config';
import videoSearchConfig from '@/panels/video_search/panel.config';
import type { PanelConfig, PanelEntry, PanelId } from '@/types/panels';
import { PANEL_IDS } from '@/types/panels';

import {
    buildExclusionMap, COMPATIBILITY_GROUPS, OPENING_CLOSES, UNIVERSAL_PANELS,
} from './exclusions';
import { isPanelId } from './panelIds';

/**
 * A panel's module downloads when the panel first renders, so the first paint waits for the
 * shell only. A panel declares the props it reads; the stage hands every panel the same untyped
 * bag out of the URL and the local props. One place to erase the difference.
 */
function entry<P extends object>(
    config: PanelConfig, load: () => Promise<ComponentType<P>>,
): PanelEntry {
    const Component = lazy(async (): Promise<{ default: PanelEntry['Component'] }> => ({
        default: (await load()) as PanelEntry['Component'],
    }));
    return { config, Component };
}

const REGISTRY = {
    agent: entry(agentConfig, async () => (await import('@/panels/agent/Panel')).AgentPanel),
    ai_chat: entry(aiChatConfig, async () => (await import('@/panels/ai_chat/Panel')).AiChatPanel),
    ai_search: entry(
        aiSearchConfig,
        async () => (await import('@/panels/ai_search/Panel')).AiSearchPanel,
    ),
    audit_log: entry(
        auditLogConfig,
        async () => (await import('@/panels/audit_log/Panel')).AuditLogPanel,
    ),
    bookmarks: entry(
        bookmarksConfig,
        async () => (await import('@/panels/bookmarks/Panel')).BookmarksPanel,
    ),
    camera_settings: entry(
        cameraSettingsConfig,
        async () => (await import('@/panels/camera_settings/Panel')).CameraSettingsPanel,
    ),
    camera_view: entry(
        cameraViewConfig,
        async () => (await import('@/panels/camera_view/Panel')).CameraViewPanel,
    ),
    cameras_list: entry(
        camerasListConfig,
        async () => (await import('@/panels/cameras_list/Panel')).CamerasListPanel,
    ),
    case_details: entry(
        caseDetailsConfig,
        async () => (await import('@/panels/case_details/Panel')).CaseDetailsPanel,
    ),
    case_document: entry(
        caseDocumentConfig,
        async () => (await import('@/panels/case_document/Panel')).CaseDocumentPanel,
    ),
    case_image: entry(
        caseImageConfig,
        async () => (await import('@/panels/case_image/Panel')).CaseImagePanel,
    ),
    case_video: entry(
        caseVideoConfig,
        async () => (await import('@/panels/case_video/Panel')).CaseVideoPanel,
    ),
    enrolled_objects: entry(
        enrolledObjectsConfig,
        async () => (await import('@/panels/enrolled_objects/Panel')).EnrolledObjectsPanel,
    ),
    events_list: entry(
        eventsListConfig,
        async () => (await import('@/panels/events_list/Panel')).EventsListPanel,
    ),
    health_monitor: entry(
        healthMonitorConfig,
        async () => (await import('@/panels/health_monitor/Panel')).HealthMonitorPanel,
    ),
    motion_list: entry(
        motionListConfig,
        async () => (await import('@/panels/motion_list/Panel')).MotionListPanel,
    ),
    notifications: entry(
        notificationsConfig,
        async () => (await import('@/panels/notifications/Panel')).LiveEventsPanel,
    ),
    objects_list: entry(
        objectsListConfig,
        async () => (await import('@/panels/objects_list/Panel')).ObjectsListPanel,
    ),
    organization_settings: entry(
        organizationSettingsConfig,
        async () => (
            await import('@/panels/organization_settings/Panel')
        ).OrganizationSettingsPanel,
    ),
    rule_estimate: entry(
        ruleEstimateConfig,
        async () => (await import('@/panels/rule_estimate/Panel')).RuleEstimatePanel,
    ),
    rule_flow: entry(
        ruleFlowConfig,
        async () => (await import('@/panels/rule_flow/Panel')).RuleFlowPanel,
    ),
    rule_flow_details: entry(
        ruleFlowDetailsConfig,
        async () => (await import('@/panels/rule_flow_details/Panel')).RuleFlowDetailsPanel,
    ),
    rules: entry(rulesConfig, async () => (await import('@/panels/rules/Panel')).RulesPanel),
    servers: entry(
        serversConfig,
        async () => (await import('@/panels/servers/Panel')).ServersPanel,
    ),
    user_details: entry(
        userDetailsConfig,
        async () => (await import('@/panels/user_details/Panel')).UserDetailsPanel,
    ),
    users: entry(usersConfig, async () => (await import('@/panels/users/Panel')).UsersPanel),
    video_search: entry(
        videoSearchConfig,
        async () => (await import('@/panels/video_search/Panel')).VideoSearchPanel,
    ),
} satisfies Record<PanelId, PanelEntry>;

/**
 * `satisfies` above is the check that matters: every id has an entry, and no
 * entry names an id the type does not. Keyed by `PanelId`, so a lookup here is
 * total — an id that reached this map has already been through `isPanelId`.
 */
export const panels: Record<PanelId, PanelEntry> = REGISTRY;

// Materialise per-panel `excludes` from the centralized coexistence
// rules. Default-deny: each panel's excludes list = every registered
// panel it is NOT compatible with (see exclusions.ts for the definition
// of "compatible").
const exclusionMap = buildExclusionMap(
    COMPATIBILITY_GROUPS, new Set(PANEL_IDS), UNIVERSAL_PANELS, OPENING_CLOSES);
for (const id of PANEL_IDS) {
    REGISTRY[id].config.excludes = exclusionMap[id] ?? [];
}

/**
 * The entry for an id nothing has checked yet — one off the URL, one restored
 * from storage, one a panel put in its own props. Misses rather than throwing.
 */
export function getPanelEntry(id: string): PanelEntry | undefined {
    return isPanelId(id) ? panels[id] : undefined;
}

/** The panels that this page offers: those the deployment switches off are left out. */
export function listPanels(): PanelConfig[] {
    return PANEL_IDS.filter(isPanelId).map((id) => REGISTRY[id].config);
}
